import React, { useEffect } from 'react';
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

import { useApplicationContext } from 'ApplicationContext';

/** Das vom Server gepushte Ereignis — nur die Felder, die die Meldung braucht. */
interface CallbackCreatedEvent {
    id: string;
    name: string;
    phone: string;
    summary: string;
}

const CALLBACK_HUB_PATH = '/hubs/callbacks';

/**
 * Unsichtbare, App-weite Komponente: hält eine SignalR-Verbindung zum Rückruf-Hub und zeigt bei
 * neuen Rückrufbitten eine native Browser-Benachrichtigung — unabhängig davon, welche Seite
 * gerade offen ist. Verbindet nur für Benutzer, die Rückrufe bearbeiten dürfen.
 *
 * Bewusst die einfache Notification-API statt Web-Push: die Meldung kommt, solange das
 * Support-System in irgendeinem Tab offen ist. Push bei geschlossenem Browser bräuchte Service
 * Worker, VAPID-Schlüssel und für iOS eine installierte PWA — ein eigenes Vorhaben.
 */
export const CallbackNotificationListener: React.FC = () => {
    const context = useApplicationContext();
    const canWork = context.hasPermission('callbacks.claim') || context.hasPermission('callbacks.complete');
    const token = context.token;
    const baseUrl = context.baseUrl;

    useEffect(() => {
        if (!canWork || !token) return;

        // Einmalig um Erlaubnis fragen; ein „denied" wird respektiert und nicht erneut gefragt.
        if ('Notification' in window && Notification.permission === 'default') {
            void Notification.requestPermission();
        }

        const connection = new HubConnectionBuilder()
            .withUrl(`${baseUrl}${CALLBACK_HUB_PATH}`, {
                accessTokenFactory: () => token,
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true,
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();

        connection.on('callbackCreated', (callback: CallbackCreatedEvent) => {
            if (!('Notification' in window) || Notification.permission !== 'granted') return;
            try {
                const notification = new Notification(`Rückrufbitte: ${callback.name}`, {
                    body: `${callback.phone}${callback.summary ? ` — ${callback.summary}` : ''}`,
                    // Gleiche Bitte = gleiche Meldung, statt Duplikate zu stapeln.
                    tag: `callback-${callback.id}`,
                });
                notification.onclick = () => {
                    window.focus();
                    window.location.href = '/rueckrufe';
                };
            } catch {
                // Manche Browser verweigern den Konstruktor in Randfällen — dann eben keine Meldung.
            }
        });

        const register = async (): Promise<void> => {
            try {
                await connection.invoke('RegisterCallbacks');
            } catch {
                // Der automatische Reconnect meldet sich über onreconnected erneut an.
            }
        };

        connection.onreconnected(() => void register());

        connection.start()
            .then(register)
            .catch(() => {
                // Kein Fehlerbanner: die Benachrichtigung ist Beiwerk, die Seite funktioniert ohne.
            });

        return () => {
            void connection.stop();
        };
    }, [canWork, token, baseUrl]);

    return null;
};

export default CallbackNotificationListener;
